import { Component, OnDestroy, OnInit, Output, EventEmitter, Inject, HostListener, ViewChild, ElementRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { AuthenticationService } from '../../core/services/auth.service';
import { environment } from '../../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { LanguageService } from '../../core/services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { HeaderSearchService } from '../../core/services/header-search.service';
import { OrdersService } from '../../core/services/orders.service';
import { CmsService } from '../../core/services/cms.service';
import jsQR from 'jsqr';
import Swal from 'sweetalert2';

interface CommandPaletteItem {
  id: string;
  title: string;
  subtitle: string;
  section: string;
  keywords: string[];
  route?: string;
  icon?: string;
  createKey?: string;
  run: () => void;
}

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent implements OnInit, OnDestroy {
  @ViewChild('commandPaletteInput') commandPaletteInput?: ElementRef<HTMLInputElement>;
  @ViewChild('scannerVideo') scannerVideo?: ElementRef<HTMLVideoElement>;

  element;
  cookieValue;
  flagvalue;
  countryName;
  valueset;
  isDarkMode = false;
  showListSearch = false;
  globalSearchText = '';
  paletteOpen = false;
  paletteQuery = '';
  selectedCommandIndex = 0;
  companyLogoUrl = '';
  scannerOpen = false;
  scannerBusy = false;
  scannerError = '';
  scannerMessage = 'Align the QR code inside the camera view.';
  private scanStream: MediaStream | null = null;
  private scanRafId: number | null = null;
  private scanCanvas: HTMLCanvasElement | null = null;
  private scanContext: CanvasRenderingContext2D | null = null;
  private hardwareScanBuffer = '';
  private hardwareScanLastKeyAt = 0;
  private hardwareScanResetTimer: any = null;

  private readonly themeStorageKey = 'ntapp-theme-mode';
  private readonly listRoutePatterns = [
    '/admin/masters',
    '/admin/roles',
    '/admin/departments',
    '/admin/customers',
    '/admin/certificate-vendors',
    '/admin/price-configurations',
    '/admin/bin',
    '/hrm/employees',
    '/order-requests',
    '/orders'
  ];
  private readonly quickActions: Array<{ key: string; title: string; subtitle: string; route: string; icon: string }> = [
    { key: 'customers', title: 'New Customer', subtitle: 'Create a customer record', route: '/admin/customers', icon: 'bx-user-plus' },
    { key: 'employees', title: 'New Employee', subtitle: 'Add an employee profile', route: '/hrm/employees', icon: 'bx-id-card' },
    { key: 'certificate-vendors', title: 'New Certificate Vendor', subtitle: 'Add a vendor and mapping', route: '/admin/certificate-vendors', icon: 'bx-certification' },
    { key: 'masters', title: 'New Master Data', subtitle: 'Add a master type record', route: '/admin/masters', icon: 'bx-collection' },
    { key: 'roles', title: 'New Role', subtitle: 'Create a new system role', route: '/admin/roles', icon: 'bx-shield-quarter' },
    { key: 'departments', title: 'New Department', subtitle: 'Add a department', route: '/admin/departments', icon: 'bx-buildings' },
    { key: 'price-configurations', title: 'New Price Configuration', subtitle: 'Create a pricing setup', route: '/admin/price-configurations', icon: 'bx-receipt' },
    { key: 'bins', title: 'New Bin', subtitle: 'Add a bin master record', route: '/admin/bin', icon: 'bx-box' },
    { key: 'order-requests', title: 'New Order Request', subtitle: 'Start a fresh order request', route: '/order-requests', icon: 'bx-message-square-add' },
    { key: 'orders', title: 'New Order', subtitle: 'Create a new order', route: '/orders', icon: 'bx-cart-add' }
  ];
  private allCommands: CommandPaletteItem[] = [];

  constructor(
    @Inject(DOCUMENT) private document: any,
    private router: Router,
    private authService: AuthenticationService,
    public languageService: LanguageService,
    public translate: TranslateService,
    public _cookiesService: CookieService,
    private headerSearchService: HeaderSearchService,
    private ordersService: OrdersService,
    private cmsService: CmsService
  ) { }

  openMobileMenu: boolean;

  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();

  ngOnInit() {
    this.openMobileMenu = false;
    this.element = document.documentElement;
    this.initializeThemeMode();
    this.loadCompanyBranding();
    this.globalSearchText = this.headerSearchService.getSearchTerm();
    this.updateHeaderSearchVisibility(this.router.url);
    this.refreshCommands();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateHeaderSearchVisibility(event.urlAfterRedirects || event.url);
        this.refreshCommands();
      }
    });

    this.getUserName();
  }

  ngOnDestroy() {
    this.closeCommandPalette();
    this.stopScanner();
    this.resetHardwareScanBuffer();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent) {
    const target = event.target as HTMLElement | null;
    const isInputTarget = !!target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
    const isContentEditable = !!target?.isContentEditable;

    if (!this.paletteOpen && !this.scannerOpen && !isInputTarget && !isContentEditable && this.tryHandleHardwareQrScan(event)) {
      return;
    }

    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.openCommandPalette();
      return;
    }

    if (!this.paletteOpen) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeCommandPalette();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveSelection(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveSelection(-1);
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey && !isInputTarget) {
      event.preventDefault();
      this.runSelectedCommand();
      return;
    }

    if (event.key === 'Enter' && isInputTarget) {
      event.preventDefault();
      this.runPaletteInputAction();
    }
  }

  getUserName() {
    const userData = JSON.parse(this.authService.currentUserData());
    return userData.firstName;
  }

  getCompanyLogo(): string {
    return this.companyLogoUrl || 'assets/images/logo-light.png';
  }

  private loadCompanyBranding(): void {
    this.cmsService.getAuthPageConfig().subscribe(
      (res: any) => {
        if (res?.isError) {
          this.companyLogoUrl = '';
          return;
        }
        this.companyLogoUrl = res?.data?.companyLogoUrl || '';
      },
      () => {
        this.companyLogoUrl = '';
      }
    );
  }

  openCommandPalette() {
    this.paletteQuery = '';
    this.refreshCommands();
    this.paletteOpen = true;
    this.selectedCommandIndex = 0;
    document.body.classList.add('command-palette-open');
    setTimeout(() => this.commandPaletteInput?.nativeElement?.focus(), 0);
  }

  closeCommandPalette() {
    this.paletteOpen = false;
    this.paletteQuery = '';
    this.selectedCommandIndex = 0;
    document.body.classList.remove('command-palette-open');
  }

  onPaletteInput(event: Event) {
    this.paletteQuery = (event.target as HTMLInputElement)?.value || '';
    this.selectedCommandIndex = 0;
  }

  onPalettePaste(event: ClipboardEvent) {
    const pastedText = String(event.clipboardData?.getData('text') || '').trim();
    if (!pastedText) {
      return;
    }
    this.paletteQuery = pastedText;
    this.selectedCommandIndex = 0;
    setTimeout(() => this.tryOpenPaletteOrderReference(pastedText, () => {
      if (this.isLikelyOrderReference(pastedText)) {
        this.showOrderReferenceNotFound(pastedText);
      }
    }));
  }

  get filteredCommands(): CommandPaletteItem[] {
    const query = (this.paletteQuery || '').trim().toLowerCase();
    const commands = [...this.allCommands];

    if (this.showListSearch) {
      const searchLabel = query
        ? `Search This List For "${this.toTitleCase(this.paletteQuery.trim())}"`
        : 'Search This List';
      commands.unshift({
        id: 'search-current-list',
        title: searchLabel,
        subtitle: 'Apply The Current List Filter On This Page',
        section: 'Search',
        keywords: ['search', 'find', 'filter', 'list', this.paletteQuery],
        icon: 'bx-search-alt',
        run: () => {
          this.globalSearchText = (this.paletteQuery || '').trim();
          this.headerSearchService.setSearchTerm(this.globalSearchText);
          this.closeCommandPalette();
        }
      });
    }

    if (!query) {
      return commands;
    }

    return commands.filter((item: CommandPaletteItem) => {
      const haystack = [
        item.title,
        item.subtitle,
        item.section,
        ...(item.keywords || [])
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }

  executePaletteCommand(command: CommandPaletteItem) {
    command.run();
  }

  isSelectedCommand(index: number): boolean {
    return index === this.selectedCommandIndex;
  }

  onCommandHover(index: number) {
    this.selectedCommandIndex = index;
  }

  getCommandPlaceholder(): string {
    return this.showListSearch ? 'Jump to pages, run quick actions, or search this list' : 'Jump to pages or run quick actions';
  }

  getPaletteHint(): string {
    return this.showListSearch ? 'Type to open menus, create records, or search the current list.' : 'Type to open menus or start common create flows.';
  }

  initializeThemeMode() {
    const savedThemeMode = localStorage.getItem(this.themeStorageKey);
    this.isDarkMode = savedThemeMode === 'dark';
    this.applyThemeMode();
  }

  toggleThemeMode() {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem(this.themeStorageKey, this.isDarkMode ? 'dark' : 'light');
    this.applyThemeMode();
  }

  applyThemeMode() {
    document.body.classList.toggle('app-dark-mode', this.isDarkMode);
  }

  toggleRightSidebar() {
    this.settingsButtonClicked.emit();
  }

  toggleMobileMenu(event: any) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/account/login']);
  }

  openQrScanner() {
    if (!this.hasCameraSupport()) {
      Swal.fire('QR scanner is not supported in this browser', '', 'warning');
      return;
    }

    this.scannerOpen = true;
    this.scannerBusy = true;
    this.scannerError = '';
    this.scannerMessage = 'Opening camera...';
    setTimeout(() => this.startScanner(), 0);
  }

  closeQrScanner() {
    this.stopScanner();
    this.scannerOpen = false;
    this.scannerBusy = false;
    this.scannerError = '';
    this.scannerMessage = 'Align the QR code inside the camera view.';
  }

  fullscreen() {
    document.body.classList.toggle('fullscreen-enable');
    if (!document.fullscreenElement && !this.element.mozFullScreenElement && !this.element.webkitFullscreenElement) {
      if (this.element.requestFullscreen) {
        this.element.requestFullscreen();
      } else if (this.element.mozRequestFullScreen) {
        this.element.mozRequestFullScreen();
      } else if (this.element.webkitRequestFullscreen) {
        this.element.webkitRequestFullscreen();
      } else if (this.element.msRequestFullscreen) {
        this.element.msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if (this.document.mozCancelFullScreen) {
        this.document.mozCancelFullScreen();
      } else if (this.document.webkitExitFullscreen) {
        this.document.webkitExitFullscreen();
      } else if (this.document.msExitFullscreen) {
        this.document.msExitFullscreen();
      }
    }
  }

  private moveSelection(direction: number) {
    const items = this.filteredCommands;
    if (!items.length) {
      this.selectedCommandIndex = 0;
      return;
    }
    const maxIndex = items.length - 1;
    const nextIndex = this.selectedCommandIndex + direction;
    if (nextIndex < 0) {
      this.selectedCommandIndex = maxIndex;
      return;
    }
    if (nextIndex > maxIndex) {
      this.selectedCommandIndex = 0;
      return;
    }
    this.selectedCommandIndex = nextIndex;
  }

  private runSelectedCommand() {
    const selected = this.filteredCommands[this.selectedCommandIndex];
    if (selected) {
      selected.run();
    }
  }

  private runPaletteInputAction() {
    const query = String(this.paletteQuery || '').trim();
    if (!query) {
      this.runSelectedCommand();
      return;
    }
    this.tryOpenPaletteOrderReference(query, () => {
      if (this.isLikelyOrderReference(query)) {
        this.showOrderReferenceNotFound(query);
        return;
      }
      this.runSelectedCommand();
    });
  }

  private isLikelyOrderReference(value: string): boolean {
    const normalized = String(value || '').trim();
    return normalized.length >= 4 && /\d/.test(normalized) && !/\s/.test(normalized);
  }

  private showOrderReferenceNotFound(value: string) {
    Swal.fire(
      'Record not found',
      `No order or order item found for "${value}".`,
      'warning'
    );
  }

  private async startScanner() {
    if (!this.scannerVideo?.nativeElement) {
      this.scannerBusy = false;
      this.scannerError = 'Camera preview is not available.';
      return;
    }

    try {
      this.scanStream = await this.requestCameraStream({
        video: { facingMode: { ideal: 'environment' } },
        audio: false
      });
      const video = this.scannerVideo.nativeElement;
      video.srcObject = this.scanStream;
      await video.play();
      this.scannerBusy = false;
      this.scannerMessage = 'Scanning QR code...';
      this.scanFrame();
    } catch {
      this.scannerBusy = false;
      this.scannerError = 'Unable to access camera.';
    }
  }

  private stopScanner() {
    if (this.scanRafId !== null) {
      cancelAnimationFrame(this.scanRafId);
      this.scanRafId = null;
    }
    if (this.scannerVideo?.nativeElement) {
      this.scannerVideo.nativeElement.pause();
      this.scannerVideo.nativeElement.srcObject = null;
    }
    if (this.scanStream) {
      this.scanStream.getTracks().forEach((track) => track.stop());
      this.scanStream = null;
    }
  }

  private scanFrame() {
    if (!this.scannerOpen || !this.scannerVideo?.nativeElement) {
      return;
    }

    const video = this.scannerVideo.nativeElement;
    if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA && video.videoWidth && video.videoHeight) {
      const context = this.getScanContext(video.videoWidth, video.videoHeight);
      if (context) {
        context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
        const imageData = context.getImageData(0, 0, video.videoWidth, video.videoHeight);
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert'
        });
        const rawValue = String(code?.data || '').trim();
        if (rawValue) {
          this.handleScannedCode(rawValue);
          return;
        }
      }

      this.scanRafId = requestAnimationFrame(() => this.scanFrame());
      return;
    }

    this.scanRafId = requestAnimationFrame(() => this.scanFrame());
  }

  private hasCameraSupport(): boolean {
    return !!(
      navigator.mediaDevices?.getUserMedia ||
      (navigator as any).getUserMedia ||
      (navigator as any).webkitGetUserMedia ||
      (navigator as any).mozGetUserMedia ||
      (navigator as any).msGetUserMedia
    );
  }

  private requestCameraStream(constraints: MediaStreamConstraints): Promise<MediaStream> {
    if (navigator.mediaDevices?.getUserMedia) {
      return navigator.mediaDevices.getUserMedia(constraints);
    }

    const legacyGetUserMedia =
      (navigator as any).getUserMedia ||
      (navigator as any).webkitGetUserMedia ||
      (navigator as any).mozGetUserMedia ||
      (navigator as any).msGetUserMedia;

    return new Promise((resolve, reject) => {
      if (!legacyGetUserMedia) {
        reject(new Error('getUserMedia not supported'));
        return;
      }
      legacyGetUserMedia.call(navigator, constraints, resolve, reject);
    });
  }

  private getScanContext(width: number, height: number): CanvasRenderingContext2D | null {
    if (!this.scanCanvas) {
      this.scanCanvas = document.createElement('canvas');
    }

    if (!this.scanContext) {
      this.scanContext = this.scanCanvas.getContext('2d', { willReadFrequently: true });
    }

    if (!this.scanContext) {
      return null;
    }

    if (this.scanCanvas.width !== width || this.scanCanvas.height !== height) {
      this.scanCanvas.width = width;
      this.scanCanvas.height = height;
    }

    return this.scanContext;
  }

  private handleScannedCode(rawValue: string) {
    this.stopScanner();
    this.scannerBusy = true;
    this.scannerMessage = 'Opening scanned record...';
    this.scannerError = '';

    this.ordersService.getByOrderItemNo(rawValue).subscribe(
      (res: any) => {
        const orderId = Number(res?.data?.orderId || 0);
        const orderItemNo = String(res?.data?.orderItemNo || rawValue).trim();
        if (!res?.isError && orderId > 0) {
          this.closeQrScanner();
          this.router.navigate(['/orders/process', orderId], {
            queryParams: { itemNo: orderItemNo }
          });
          return;
        }
        this.openScannedOrder(rawValue);
      },
      () => this.openScannedOrder(rawValue)
    );
  }

  private tryHandleHardwareQrScan(event: KeyboardEvent): boolean {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      this.resetHardwareScanBuffer();
      return false;
    }

    const now = Date.now();
    if (this.hardwareScanLastKeyAt && now - this.hardwareScanLastKeyAt > 100) {
      this.resetHardwareScanBuffer();
    }
    this.hardwareScanLastKeyAt = now;

    if (event.key === 'Enter') {
      const scannedValue = this.hardwareScanBuffer.trim();
      this.resetHardwareScanBuffer();
      if (scannedValue.length >= 6) {
        event.preventDefault();
        this.handleScannedCode(scannedValue);
        return true;
      }
      return false;
    }

    if (event.key.length !== 1) {
      return false;
    }

    this.hardwareScanBuffer += event.key;
    this.scheduleHardwareScanReset();
    return false;
  }

  private scheduleHardwareScanReset() {
    if (this.hardwareScanResetTimer) {
      clearTimeout(this.hardwareScanResetTimer);
    }
    this.hardwareScanResetTimer = setTimeout(() => this.resetHardwareScanBuffer(), 150);
  }

  private resetHardwareScanBuffer() {
    this.hardwareScanBuffer = '';
    this.hardwareScanLastKeyAt = 0;
    if (this.hardwareScanResetTimer) {
      clearTimeout(this.hardwareScanResetTimer);
      this.hardwareScanResetTimer = null;
    }
  }

  private openScannedOrder(orderNo: string) {
    this.ordersService.getByOrderNo(orderNo).subscribe(
      (res: any) => {
        const orderId = Number(res?.data?.id || 0);
        if (!res?.isError && orderId > 0) {
          this.closeQrScanner();
          this.router.navigate(['/orders/process', orderId]);
          return;
        }
        this.scannerBusy = false;
        this.scannerError = 'Scanned QR code does not match an order or order item.';
      },
      () => {
        this.scannerBusy = false;
        this.scannerError = 'Scanned QR code does not match an order or order item.';
      }
    );
  }

  private tryOpenPaletteOrderReference(rawValue: string, onNotFound?: () => void) {
    const value = String(rawValue || '').trim();
    if (!value) {
      if (onNotFound) {
        onNotFound();
      }
      return;
    }

    this.ordersService.getByOrderItemNo(value).subscribe(
      (itemRes: any) => {
        const orderId = Number(itemRes?.data?.orderId || 0);
        const orderItemNo = String(itemRes?.data?.orderItemNo || value).trim();
        if (!itemRes?.isError && orderId > 0) {
          this.closeCommandPalette();
          this.router.navigate(['/orders/process', orderId], {
            queryParams: { itemNo: orderItemNo }
          });
          return;
        }
        this.openPaletteOrderByOrderNo(value, onNotFound);
      },
      () => this.openPaletteOrderByOrderNo(value, onNotFound)
    );
  }

  private openPaletteOrderByOrderNo(orderNo: string, onNotFound?: () => void) {
    this.ordersService.getByOrderNo(orderNo).subscribe(
      (orderRes: any) => {
        const orderId = Number(orderRes?.data?.id || 0);
        if (!orderRes?.isError && orderId > 0) {
          this.closeCommandPalette();
          this.router.navigate(['/orders/process', orderId]);
          return;
        }
        if (onNotFound) {
          onNotFound();
        }
      },
      () => {
        if (onNotFound) {
          onNotFound();
        }
      }
    );
  }

  private updateHeaderSearchVisibility(url: string) {
    const cleanUrl = (url || '').split('?')[0].toLowerCase();
    this.showListSearch = this.listRoutePatterns.some((pattern) =>
      cleanUrl === pattern || cleanUrl.startsWith(`${pattern}/`)
    );
  }

  private refreshCommands() {
    this.allCommands = [
      ...this.buildQuickActionCommands(),
      ...this.buildMenuCommands()
    ];
  }

  private buildQuickActionCommands(): CommandPaletteItem[] {
    return this.quickActions.map((item) => ({
      id: `create-${item.key}`,
      title: this.toTitleCase(item.title),
      subtitle: this.toTitleCase(item.subtitle),
      section: 'Quick Actions',
      keywords: ['new', 'add', 'create', item.key, item.title],
      route: item.route,
      createKey: item.key,
      icon: item.icon,
      run: () => {
        this.router.navigate([item.route]).then(() => {
          this.headerSearchService.requestCreateCommand(item.key);
        });
        this.closeCommandPalette();
      }
    }));
  }

  private buildMenuCommands(): CommandPaletteItem[] {
    return this.flattenMenuItems(this.getPermittedMenus());
  }

  private flattenMenuItems(items: any[], trail: string[] = []): CommandPaletteItem[] {
    const commands: CommandPaletteItem[] = [];
    for (const item of items || []) {
      const label = this.normalizeLabel(item?.label || item?.text || '');
      const nextTrail = label ? [...trail, label] : [...trail];
      if (item?.link) {
        commands.push({
          id: `menu-${item.link}`,
          title: this.toTitleCase(label),
          subtitle: this.toTitleCase(nextTrail.slice(0, -1).join(' / ') || 'Menu'),
          section: 'Navigation',
          keywords: [label, item.link, ...nextTrail],
          route: item.link,
          icon: item?.icon || 'bx-right-arrow-alt',
          run: () => {
            this.router.navigate([item.link]);
            this.closeCommandPalette();
          }
        });
      }

      if (Array.isArray(item?.subItems) && item.subItems.length) {
        commands.push(...this.flattenMenuItems(item.subItems, nextTrail));
      }
      if (Array.isArray(item?.submenu) && item.submenu.length) {
        commands.push(...this.flattenMenuItems(item.submenu, nextTrail));
      }
    }
    return commands;
  }

  private getPermittedMenus(): any[] {
    try {
      const menus = JSON.parse(localStorage.getItem('menus') || '[]');
      const permissions = JSON.parse(localStorage.getItem('permissions') || '{}');
      const roles = JSON.parse(localStorage.getItem('roleNames') || '[]');
      const role = Array.isArray(roles) ? roles[0] : null;
      const permissionList = role ? permissions?.[role] || [] : [];
      const allowedModules = [
        ...new Set(
          (permissionList || []).map((permission: string) => {
            const lastIndex = permission.lastIndexOf('_');
            return lastIndex > -1 ? permission.substring(0, lastIndex) : permission;
          })
        )
      ];

      const filterMenuList = (source: any[]): any[] =>
        (source || [])
          .map((menu: any) => {
            const nextMenu = { ...menu };
            if (Array.isArray(nextMenu.submenu) && nextMenu.submenu.length) {
              nextMenu.submenu = filterMenuList(nextMenu.submenu);
            }
            if (Array.isArray(nextMenu.subItems) && nextMenu.subItems.length) {
              nextMenu.subItems = filterMenuList(nextMenu.subItems);
            }

            const menuLabel = String(nextMenu.label || '').trim();
            if (
              !allowedModules.length ||
              allowedModules.includes(menuLabel) ||
              (nextMenu.submenu && nextMenu.submenu.length) ||
              (nextMenu.subItems && nextMenu.subItems.length) ||
              !!nextMenu.link
            ) {
              return nextMenu;
            }
            return null;
          })
          .filter(Boolean);

      return filterMenuList(menus);
    } catch (error) {
      return [];
    }
  }

  private normalizeLabel(value: string): string {
    const cleaned = String(value || '')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[./_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return this.toTitleCase(cleaned);
  }

  private toTitleCase(value: string): string {
    return String(value || '')
      .split(' ')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }
}
